/**
 * Normalizes a role entry from auth state to a comparable slug string.
 * @param {string|{ name?: string }|null|undefined} entry - Role string or object with `name`.
 * @returns {string} Role slug or empty string.
 */
export function debtCollectionRoleSlug(entry) {
  if (entry == null || entry === "") return "";
  if (typeof entry === "string") return entry;
  if (typeof entry === "object" && entry.name != null) {
    return String(entry.name);
  }
  return "";
}

/**
 * Whether the user has super-admin role for debt collection features.
 * @param {unknown} roles - `user.roles` from auth state.
 * @returns {boolean} True if any role is super-admin.
 */
export function isDebtCollectionSuperAdmin(roles) {
  const list = Array.isArray(roles) ? roles : [];
  return list.some((r) => debtCollectionRoleSlug(r) === "super-admin");
}

/**
 * Whether the user has franchise-owner role for debt collection features.
 * @param {unknown} roles - `user.roles` from auth state.
 * @returns {boolean} True if any role is franchise-owner.
 */
export function isDebtCollectionFranchiseOwner(roles) {
  const list = Array.isArray(roles) ? roles : [];
  return list.some((r) => debtCollectionRoleSlug(r) === "franchise-owner");
}

/**
 * Whether the user may open debt collection settings and list flows.
 * @param {unknown} roles - `user.roles` from auth state.
 * @returns {boolean} True for super-admin or franchise-owner.
 */
export function canAccessDebtCollectionFlow(roles) {
  return (
    isDebtCollectionSuperAdmin(roles) ||
    isDebtCollectionFranchiseOwner(roles)
  );
}

/**
 * Franchise id forced onto debt list queries for franchise owners; null for super-admins or unknown users.
 * @param {{ roles?: unknown, franchise_id?: string|number, franchiseId?: string|number }|null|undefined} user - Auth user object.
 * @returns {string|null} Trimmed franchise id or null when not scoped.
 */
export function getDebtListScopedFranchiseId(user) {
  if (!user) return null;
  const roles = user.roles;
  if (isDebtCollectionSuperAdmin(roles)) return null;
  if (!isDebtCollectionFranchiseOwner(roles)) return null;
  const fid = user.franchise_id ?? user.franchiseId;
  if (fid == null || fid === "") return null;
  const s = String(fid).trim();
  return s || null;
}
